import React from 'react';
import { RtcAudioClient } from './audio';

export const roomField = (room, key) => room?.get?.(key) ?? room?.[key];

export const roomHasEnded = room => {
  const endedAt = roomField(room, 'ended_at');
  return !!endedAt && !String(endedAt).startsWith('0001-');
};

const defaultClientFactory = options => new RtcAudioClient(options);

const initialSession = (ended, websocketUrl) => ({
  status: ended ? 'ended' : websocketUrl ? 'available' : 'error',
  mic: 'off',
  live: true,
  error: websocketUrl || ended ? null : 'missing_url',
});

/**
 * React-owned RTC lifecycle. Nothing connects until join() is called; changing
 * rooms and unmounting both destroy every resource owned by the current client.
 */
export default function useRtcAudioRoom({ room, clientFactory = defaultClientFactory }) {
  const websocketUrl = roomField(room, 'websocket_url');
  const startedAt = roomField(room, 'started_at');
  const endedAt = roomField(room, 'ended_at');
  const ended = roomHasEnded(room);
  const clientRef = React.useRef(null);
  const [session, setSession] = React.useState(() => initialSession(ended, websocketUrl));
  const [duration, setDuration] = React.useState(0);
  const [playhead, setPlayhead] = React.useState(0);
  const [activity, setActivity] = React.useState({ speaking: false, level: 0 });

  const reset = React.useCallback(() => {
    setSession(initialSession(ended, websocketUrl));
    setActivity({ speaking: false, level: 0 });
  }, [ended, websocketUrl]);

  const leave = React.useCallback(() => {
    clientRef.current?.destroy();
    clientRef.current = null;
    reset();
  }, [reset]);

  React.useEffect(() => {
    // URL changes can also change actor_id, so the old socket must never survive.
    clientRef.current?.destroy();
    clientRef.current = null;
    reset();
    return () => {
      clientRef.current?.destroy();
      clientRef.current = null;
    };
  }, [websocketUrl, startedAt, endedAt, reset]);

  React.useEffect(() => {
    if (!room || !startedAt) return undefined;
    const update = () => {
      const start = Date.parse(startedAt);
      const finish = endedAt ? Date.parse(endedAt) : Date.now();
      const next = Number.isFinite(start) && Number.isFinite(finish)
        ? Math.max(0, (finish - start) / 1000)
        : 0;
      setDuration(next);
      if (session.live) setPlayhead(next);
    };
    update();
    if (endedAt) return undefined;
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, [room, startedAt, endedAt, session.live]);

  const join = React.useCallback(() => {
    if (!websocketUrl || ended || session.status === 'connecting') return;
    clientRef.current?.destroy();
    let client;
    client = clientFactory({
      websocketUrl,
      startedAt,
      endedAt,
      onState: next => {
        if (clientRef.current === client) setSession(next);
      },
      onPlayhead: next => {
        if (clientRef.current === client) setPlayhead(next);
      },
      onActivity: next => {
        if (clientRef.current === client) setActivity(next);
      },
    });
    clientRef.current = client;
    client.connect();
  }, [clientFactory, websocketUrl, startedAt, endedAt, ended, session.status]);

  const setMicrophone = React.useCallback(
    enabled => clientRef.current?.setMicrophone(enabled),
    []
  );
  const rewindToStart = React.useCallback(
    () => clientRef.current?.rewindToStart(),
    []
  );
  const goLive = React.useCallback(
    () => clientRef.current?.goLive(),
    []
  );
  const seekToElapsed = React.useCallback(
    seconds => clientRef.current?.seekToElapsed(seconds),
    []
  );

  return {
    activity,
    duration,
    ended,
    goLive,
    join,
    leave,
    playhead,
    rewindToStart,
    seekToElapsed,
    session,
    setMicrophone,
    websocketUrl,
  };
}
